import React, {useState} from "react";
import {
    CRITERIA_MODEL,
    DEFAULT_LEFT,
    SEARCH_MODEL
} from "../../../../components/table/Model/TableModel";
import {SearchByFilters, SimpleSearchWithId} from "../../../../reduxToolkit/Search/SearchApi";
import TableFunction from "../../../../components/table/TableFunction";
import {useDispatch, useSelector} from "react-redux";
import {TABLES} from "../../../../Apis/Tables";
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import {status, STATUS_MODEL} from "../../../../enums/statusEnum";
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";
import {
    CheckIconsOptions,
    DeleteIconsOptions,
    PlusIconsOptions,
    UpdateIconsOptions
} from "../../../../components/buttons/IconButtons";
import {permissionObj} from "../../../../Helpers/permission";
import routes from "../../../../Routes/routes";
import CustomChip from "../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum";
import {EMPTY_VALUES} from "../../../../Apis/functions";
import {VIEW_TYPE_LOGISTICS} from "../../../../enums/ViewType";

/**
 * @fileoverview RequestTabApp handles the RequestTabApp tab for logistic requests.
 * It fetches and displays transport requests for a given supplier, and renders available actions
 * based on request status and user permissions.
 *
 * @module RequestTabApp
 * @author Ferro Zapata
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */
/**
 * RequestTabApp component
 *
 * Displays and manages the RequestTabApp transport requests tab with filtering, searching,
 * expandable row details, and dynamic option menus based on request status and permissions.
 *
 * @param {Object} props - Component props
 * @param {number} props.supplierId - Supplier user ID (null if not a supplier)
 * @param {Function} props.setShowDeleteModal - Sets visibility for the delete modal
 * @param {Function} props.setShowAcceptRequest - Sets visibility for accept request modal
 * @param {Function} props.setShowDepartureComment - Sets visibility for departure comment modal
 * @param {Function} props.setRow - Sets the currently selected row
 * @param {boolean} props.modified - Flag to re-fetch data
 * @param {Function} props.setModified - Sets the modified flag
 *
 * @returns {Object} An object with state variables and handler functions for table and UI rendering
 */


export const RequestTabApp = ({
                                  supplierId,
                                  setShowDeleteModal,
                                  setShowAcceptRequest,
                                  setShowDepartureComment,
                                  setRow,
                                  modified,
                                  setModified
                              }) => {
    const dispatch = useDispatch();

    const [selectedList, setSelectedList] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [searchResults, setSearchResults] = useState('');
    const [bodyForExport, setBodyForExport] = useState();
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const searchId = 'txt_af_search_transport_request';
    const option = TABLES.LOGISTICREQUEST;
    const model = TABLES.LOGISTICREQUEST;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const [finalArray, setFinalArray] = useState([]);

    /**
     * Closes the currently open menu.
     */
    const renderOptionsMenu = (row) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle}
                                     menuId={`menu-${row.id}`} isAccordion={true}>
                    {supplierId ?
                        <>
                            {[STATUS_MODEL.REQUESTED, STATUS_MODEL.ASSIGNED].includes(row.statusId) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('accept')}
                                    click={() => {
                                        setShowAcceptRequest(true);
                                        setRow(row)
                                    }}
                                    icon={<CheckIconsOptions/>}
                                />)
                            }
                        </> :
                        <>
                            {([STATUS_MODEL.REQUESTED, STATUS_MODEL.ASSIGNED].includes(row.statusId))
                            && (DEV || permission?.includes(permissionObj.WEB_SERVICE_TYPE_UPDATE))
                            && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('update')}
                                    url={`${routes.transportRequest.update}${row.id}`}
                                    icon={<UpdateIconsOptions/>}
                                />
                            )}
                            {([STATUS_MODEL.REQUESTED, STATUS_MODEL.ASSIGNED].includes(row.statusId)
                                && row?.SupplierEmployeeId !== null
                                && row?.supplierVehicleId !== null
                            ) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('add_departure_comment')}
                                    click={() => {
                                        setShowDepartureComment(true);
                                        setRow(row);
                                    }}
                                    icon={<PlusIconsOptions className="icon-color-general-950"/>}
                                />
                            )}
                            {((DEV || permission?.includes(permissionObj?.WEB_SERVICE_TYPE_DELETE))
                                && [STATUS_MODEL.REQUESTED, STATUS_MODEL.ASSIGNED, STATUS_MODEL.DELIVERED,
                                    STATUS_MODEL.CANCELED, STATUS_MODEL.REJECTED].includes(row.statusId)) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('delete')}
                                    click={() => {
                                        setSelectedList([row.id])
                                        setShowDeleteModal(true);
                                    }}
                                    icon={<DeleteIconsOptions/>}
                                    fontColor='font-color-error-600'
                                    iconColor={"icon-color-error-code-600"}
                                />
                            )}
                        </>
                    }
                </DropDownMenuProfile>
            </>
        )
    };

    /**
     * Sets the expanded row to show additional information.
     * @param {Object} row - The row to expand
     */
    const columns = [
        {field: 'item', label: 'item', fixed: true, left: DEFAULT_LEFT},
        {field: 'originCenterName', label: 'origin_center_name'},
        {field: 'destinationCenterName', label: 'destination_center_name'},
        {field: 'departureAt', label: 'pickup_date'},
        {field: 'deliveryAt', label: 'delivery_date'},
        {
            field: 'statusId', label: 'status',
            render: (value) => {
                return (
                    <CustomChip
                        background={statusColor[value]}
                        fontColor={statusFontColor[value]}
                        borderColor={statusBorderColor[value]}
                        legend={t(status[value]) || EMPTY_VALUES.DEFAULT} variant={'large'}
                    />
                );
            }
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model,
            userId: supplierId,
            viewType: VIEW_TYPE_LOGISTICS.REQUESTED,
            key:searchElement
        });

        setBodyForExport(body);
        dispatch(SimpleSearchWithId({body})).then(
            ({payload: {data: {data, success}}}) => {
                if (success === true) {
                    setSearchResults(data);
                }
            }
        );
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model,
            userId: supplierId,
            viewType: VIEW_TYPE_LOGISTICS.REQUESTED,
            criteriaList
        });
        dispatch(SearchByFilters({option, body})).then(({payload: {data: {data}}}) => {
            setSearchResults(data);
        });
    }

    const {t, handleCheckboxChange, handleDeleteAll, handleClose, handleRowExpand, handleMenuToggle, pagination, openMenu, page, rowsPerPage
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: searchResults,
        labelRowsPerPage: 'transport_request_per_page',
        hasContainer : true,
        fixedPagination : false,

        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray//New - apply only with advanced search
    });



    return {
        searchElement,
        setSearchElement,
        setBodyForExport,
        setSearchResults,
        option,
        page,
        rowsPerPage,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        searchResults,
        selectedList,
        searchId,
        columns,
        handleCheckboxChange,
        pagination,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        t
    };
};


